import React, { useState, useEffect, Fragment, useLayoutEffect } from "react";
import LoadingIndicator from "../shared/LoadingIndicator";
import StickyBar from "../shared/StickyBar";
import Filter from "../shared/Filter";
import Fuse from "fuse.js";
import axios from "../shared/axios";
import debounce from "debounce";
import { format } from "date-fns";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Card,
  CardBody,
  ListGroup,
  Row,
  Col,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  ModalFooter,
  UncontrolledTooltip,
  CardHeader,
} from "reactstrap";
import { X, Plus } from "react-feather";

export default function ProducerEventSelector(props) {
  const { updateEventFields, formState, saved } = props;
  const { graphicEnabled, orgEnabled, eventsUrl, eventMax, currentTeam } =
    props.events || {};
  const [events, setEvents] = useState([]);
  const [groupedEvents, setGroupedEvents] = useState({});
  const [loadedEvents, setLoadedEvents] = useState(false);
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState(true);
  const [query, setQuery] = useState("");

  const multi = graphicEnabled == "multi";

  const addEventData = (events) => {
    return events.map((event) => {
      return {
        ...event,
        fixed_start_time: new Date(event.fixed_start_time),
        fixed_display_time_zone: event.fixed_display_time_zone,
        fixed_start_time_with_zone: event.fixed_start_time_with_zone,
        fixed_start_time_without_zone: event.fixed_start_time_without_zone,
      };
    });
  };

  const groupEventsByMonth = (events) => {
    const grouped = {};
    
    events.forEach((event) => {
      // Use fixed_start_time for consistent timezone handling
      const eventDate = new Date(event.fixed_start_time);
      const monthKey = format(eventDate, "yyyy-MM");
      const dayKey = format(eventDate, "yyyy-MM-dd");
      
      if (!grouped[monthKey]) {
        grouped[monthKey] = {
          month: format(eventDate, "MMMM yyyy"),
          days: {}
        };
      }
      
      if (!grouped[monthKey].days[dayKey]) {
        grouped[monthKey].days[dayKey] = [];
      }
      
      grouped[monthKey].days[dayKey].push(event);
    });
    
    // Sort days within each month and events within each day
    Object.keys(grouped).forEach((monthKey) => {
      const monthData = grouped[monthKey];
      const sortedDays = {};
      
      // Sort day keys chronologically
      Object.keys(monthData.days)
        .sort()
        .forEach((dayKey) => {
          // Sort events within each day by start time
          sortedDays[dayKey] = monthData.days[dayKey].sort((a, b) => 
            new Date(a.fixed_start_time) - new Date(b.fixed_start_time)
          );
        });
      
      grouped[monthKey].days = sortedDays;
    });
    
    return grouped;
  };

  const [linkedEvents, setLinkedEvents] = useState(
    props.events && props.events.events
      ? addEventData(props.events.events)
      : [],
  );
  const [updatedLinkedEvents, setUpdatedLinkedEvents] = useState(linkedEvents);

  const debouncedSetQuery = debounce(setQuery, 200);

  const loadEvents = async () => {
    const response = await axios.get(eventsUrl);
    const eventsWithData = addEventData(response.data.events).sort((a, b) => 
      new Date(a.fixed_start_time) - new Date(b.fixed_start_time)
    );
    setEvents(eventsWithData);
    setGroupedEvents(groupEventsByMonth(eventsWithData));
    setLoadedEvents(true);
  };

  useEffect(() => {
    if (open && !loadedEvents) {
      loadEvents();
    }
  }, [open]);

  useLayoutEffect(() => {
    if (!open && events.length && linkedEvents.length) {
      updateEventFields();
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setUpdatedLinkedEvents(linkedEvents);
    }
  }, [open]);

  const scopedEvents = scope
    ? events.filter((event) => {
        return event.owner_type == "Team" && event.owner_id == currentTeam.id;
      })
    : events;

  const scopedGroupedEvents = scope
    ? groupEventsByMonth(scopedEvents)
    : groupedEvents;

  const selectEvent = (event) => {
    if (multi) {
      setUpdatedLinkedEvents([...updatedLinkedEvents, event]);
    } else {
      setSingleEvent(event);
    }
  };

  const deselectEvent = (event) => {
    setUpdatedLinkedEvents(updatedLinkedEvents.filter((e) => e.id != event.id));
  };

  const setSingleEvent = (event) => {
    setLinkedEvents([event]);
    setOpen(false);
  };

  const setMultiEvents = () => {
    setLinkedEvents(updatedLinkedEvents);
    setOpen(false);
  };

  const fuseOptions = {
    keys: ["name", "venue"],
    threshold: 0.5,
    ignoreLocation: true,
  };

  const fuse = new Fuse(scopedEvents, fuseOptions);
  const queriedEvents = query
    ? fuse.search(query).map((result) => result.item)
    : scopedEvents;
  
  // Filter the already-grouped events instead of re-grouping
  const filterGroupedEvents = (groupedEvents, filteredEvents) => {
    if (!query) return groupedEvents;
    
    const filteredEventIds = new Set(filteredEvents.map(event => event.id));
    const filteredGrouped = {};
    
    Object.keys(groupedEvents).forEach((monthKey) => {
      const monthData = groupedEvents[monthKey];
      const filteredDays = {};
      
      Object.keys(monthData.days).forEach((dayKey) => {
        const dayEvents = monthData.days[dayKey];
        const filteredDayEvents = dayEvents.filter(event => filteredEventIds.has(event.id));
        
        if (filteredDayEvents.length > 0) {
          filteredDays[dayKey] = filteredDayEvents;
        }
      });
      
      if (Object.keys(filteredDays).length > 0) {
        filteredGrouped[monthKey] = {
          month: monthData.month,
          days: filteredDays
        };
      }
    });
    
    return filteredGrouped;
  };
  
  const queriedGroupedEvents = filterGroupedEvents(scopedGroupedEvents, queriedEvents);

  // When saved, group only the linked events
  const linkedGroupedEvents = saved ? groupEventsByMonth(linkedEvents) : queriedGroupedEvents;

  const showSelectorButton = graphicEnabled && orgEnabled;

  const displayForEvent = (event, showTeam = false, showTimeProminently = false) => {
    const timeDisplay = event.fixed_start_time.toLocaleString("en-us", {
      timeStyle: "short",
      timeZone: event.fixed_display_time_zone || "America/Indianapolis",
    });
    
    return (
      <>
        <strong className="d-block">{event.name}</strong>
        {showTeam && event.team_name && (
          <div>
            {event.team_name && (
              <h5 className="all-unset">
                <span className="fs-6 fw-bold text-secondary">{event.team_name}</span>
              </h5>
            )}
          </div>
        )}
        <small className="text-muted">
          {showTimeProminently ? (
            <>
              <span className="fw-bold text-primary">{timeDisplay}</span>
            </>
          ) : (
            <>
              {timeDisplay}
            </>
          )}
        </small>
      </>
    );
  };

  const eventComponent = (event, showTeam = false, showTimeProminently = false) => {
    // When saved, all events shown are linked events, so they should be considered selected
    const selected = saved ? true : updatedLinkedEvents.some((e) => e.id == event.id);
    return (
      <div key={event.id} className={`event-row ${selected ? "bg-lightest" : ""}`}>
        <div className="d-flex align-items-center">
          <div className="flex-grow-1">{displayForEvent(event, showTeam, showTimeProminently)}</div>
          {!saved && (
            <div xs="auto">
              {selected ? (
                <Button
                  className="d-flex align-items-center p-1"
                  color="outline-danger"
                  style={{ "aspect-ratio": "1 / 1" }}
                  size="sm"
                  onClick={() => deselectEvent(event)}
                >
                  <X size="16" /><span className="visually-hidden">Remove</span>
                </Button>
              ) : eventMax && updatedLinkedEvents.length >= eventMax ? (
                <>
                  <Button
                    className="d-flex align-items-center"
                    color="outline-primary disabled"
                    size="sm"
                    id={`max-reached-${event.id}`}
                  >
                    Unavailable
                  </Button>
                  <UncontrolledTooltip
                    fade={false}
                    delay={{ hide: 0, show: 0 }}
                    placement="top"
                    target={`max-reached-${event.id}`}
                  >
                    Maximum of {eventMax} events for this graphic have been
                    selected.
                  </UncontrolledTooltip>
                </>
              ) : (
                <Button
                  className="d-flex align-items-center p-1"
                  style={{ "aspect-ratio": "1 / 1" }}
                  color="primary"
                  size="sm"
                  onClick={() => selectEvent(event)}
                >
                  <Plus size="16" /><span className="visually-hidden">Select</span>
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderGroupedEvents = () => {
    return Object.keys(linkedGroupedEvents).map((monthKey) => {
      const monthData = linkedGroupedEvents[monthKey];
      return (
        <div key={monthKey} className="card rounded-1 mb-3 mb-md-4 position-relative">
          <div className="card-header card-header-month rounded-top-1 d-flex px-2 py-1 px-sm-3 py-sm-2">
            <h4 className="all-unset">
              <span className="d-block fs-6 fs-md-5 fw-bold">{monthData.month}</span>
            </h4>
          </div>
          <div>
            {Object.keys(monthData.days).map((dayKey, index) => {
              const dayEvents = monthData.days[dayKey];
              // Use the first event's fixed_start_time to get the correct timezone-aware date
              const firstEvent = dayEvents[0];
              const dayDate = new Date(firstEvent.fixed_start_time);
              const dayName = dayDate.toLocaleDateString("en-us", {
                weekday: "long",
                month: "long",
                day: "numeric",
                timeZone: firstEvent.fixed_display_time_zone || "America/Indianapolis",
              });
              const showTimeProminently = dayEvents.length > 1;
              
              return (
                <div key={dayKey} className={`events-day ${index > 0 ? "border-top" : ""}`}>
                  <div className="border-end text-center position-relative">
                    <div className="mb-0 mb-md-3">
                      <h3 className="all-unset" aria-label={format(dayDate, "EEEE d")}>
                        <div className="fs-5 fs-md-4 fw-bold lh-1 mt-2 mt-md-3">
                          {format(dayDate, "d")}
                        </div>
                        <div className="text-secondary fs-7 fs-md-6 mb-0 mb-md-2 text-uppercase">
                          {format(dayDate, "EEE")}
                        </div>
                      </h3>
                    </div>
                  </div>
                  <div className="card-body p-0 d-flex align-items-stretch w-100 flex-wrap">
                    {dayEvents.map((event) => eventComponent(event, !scope, showTimeProminently))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    });
  };

  return showSelectorButton ? (
    <>
      {linkedEvents.length == 0 && (
        <Button
          block
          color="primary"
          className="mb-3"
          onClick={() => setOpen(true)}
          disabled={formState.updatingFields}
        >
          Connect Event{multi ? "s" : ""}…
        </Button>
      )}
      {!multi && linkedEvents.length > 0 && (
        <>
          <h5 className="mb-1 text-dark">
            Connected Event{linkedEvents.length > 1 ? "s" : ""}
          </h5>
          <Card className="rounded bg-gray mb-3">
            <CardBody className="card-body-sm">
              <Row className="align-items-center">
                <Col>{displayForEvent(linkedEvents[0])}</Col>
                <Col xs="auto">
                  {saved ? (
                    <Button
                      color="outline-secondary"
                      size="sm"
                      onClick={() => setOpen(true)}
                    >
                      View
                    </Button>
                  ) : (
                    <Button
                      color="outline-secondary"
                      size="sm"
                      onClick={() => {
                        setLinkedEvents([]);
                        setUpdatedLinkedEvents([]);
                      }}
                    >
                      Clear
                    </Button>
                  )}
                </Col>
              </Row>
            </CardBody>
          </Card>
        </>
      )}
      {multi && linkedEvents.length > 0 && (
        <>
          <Card className="rounded bg-gray mb-3">
            <CardBody className="card-body-sm">
              <Row className="align-items-center gap-2">
                <Col xs={saved ? 12 : "auto"}>
                {!saved ? (
                  <strong>
                    {linkedEvents.length} Event{linkedEvents.length > 1 ? "s" : ""} Linked
                  </strong>
                ) : (
                  <>
                    <p className="mb-0 text-pretty"><strong>Saved graphics can't change linked events.</strong></p>
                    <p className="mb-0 text-pretty">Create a new graphic to link different events.</p>
                  </>
                )}
                </Col>
                <Col xs="auto">
                  <Button
                    color="outline-secondary"
                    size="sm"
                    onClick={() => setOpen(true)}
                  >
                    {saved ? (`View ${linkedEvents.length} Linked Event${linkedEvents.length > 1 ? "s" : ""}`) : "Edit"}
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </>
      )}
      {linkedEvents.map((event) => (
        <input
          key={`linked-${event.id}`}
          type="hidden"
          name="graphic[event_ids][]"
          value={event.id}
        />
      ))}
      <Modal
        size="lg"
        className="h-100"
        contentClassName="h-100"
        isOpen={open}
        toggle={() => setOpen(!open)}
      >
        <ModalHeader
          toggle={() => setOpen(!open)}
          tag="h4"
          className="modal-header-segmented"
        >
          {saved
            ? `Linked Event${linkedEvents.length == 1 ? "" : "s"}`
            : multi
              ? `Select ${eventMax ? `up to ${eventMax}` : ""} Events`
              : "Select an Event"}
        </ModalHeader>
        <ModalBody
          className={`position-relative pt-0 bg-gray ${loadedEvents ? "" : "modal-body-minheight"}`}
        >
          <LoadingIndicator
            loading={!loadedEvents}
            title="Loading events…"
            className="spinner-fill-sm"
          >
            {!saved && (
              <StickyBar
                className="mb-2rem"
                contentClasses="p-2 gap-2 grid d-lg-flex"
                customGrid={true}
              >
                <Filter
                  title="Filter events…"
                  query={query}
                  onChange={debouncedSetQuery}
                  className="g-col-12 flex-grow-1 ps-2"
                />
                <div className="g-col-6">
                  <UncontrolledDropdown>
                    <DropdownToggle
                      color="link"
                      className="d-flex align-items-center mw-100"
                      caret
                    >
                      <div className="text-truncate mw-100 d-inline-block">
                        {scope
                          ? `${currentTeam.name} Events`
                          : "All Organization Events"}
                      </div>
                    </DropdownToggle>
                    <DropdownMenu end={true}>
                      <DropdownItem onClick={() => setScope(false)}>
                        All Organization Events
                      </DropdownItem>
                      <DropdownItem onClick={() => setScope(true)}>
                        {currentTeam.name} Events
                      </DropdownItem>
                    </DropdownMenu>
                  </UncontrolledDropdown>
                </div>
              </StickyBar>
            )}
            {saved ? (
              <div className="my-4">
                <div className="alert alert-warning mb-4">
                  <h2 className="all-unset">
                    <span className="fs-5">There are {linkedEvents.length} linked events for this graphic</span>
                  </h2>
                  <p className="mb-0 text-pretty fw-normal">Create a new graphic to link different events.</p>
                </div>
                {renderGroupedEvents()}
              </div>
            ) : queriedEvents.length ? (
              <Fragment>
                <h4 className="mt-4">Upcoming Events</h4>
                {renderGroupedEvents()}
              </Fragment>
            ) : (
              <div className="card card-empty">
                <div className="card-body">
                  {query
                    ? "No events match your filter."
                    : "No future published events found."}
                </div>
              </div>
            )}
          </LoadingIndicator>
        </ModalBody>
        {multi && !saved && (
          <ModalFooter>
            <Row className="w-100 px-0">
              <Col className="px-0">
                <Button
                  color="primary"
                  className="w-100"
                  disabled={updatedLinkedEvents.length === 0}
                  onClick={setMultiEvents}
                >
                  Link {updatedLinkedEvents.length > 0 ? updatedLinkedEvents.length : ''} Event{updatedLinkedEvents.length !== 1 ? 's' : ''}
                </Button>
              </Col>
              <Col xs="auto" className="pe-0">
                <Button
                  color="outline-secondary"
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </Button>
              </Col>
            </Row>
          </ModalFooter>
        )}
      </Modal>
    </>
  ) : null;
}